import {
  PageContent,
  SplitDefinition,
  SplittingSuggestion, // Import the new type
  StratifyDocument,
} from "@stratify/shared";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  QuerySnapshot,
  where,
} from "firebase/firestore";
import { db } from "../../../config/firebase";
import apiClient from "../../../shared/apiClient";

/**
 * Client for interacting with the documents API
 */

/**
 * Lists all documents for a user
 */
export const listDocuments = async (
  userId: string
): Promise<StratifyDocument[]> => {
  const documentsRef = collection(db, "documents");
  const q = query(documentsRef, where("uploadedBy", "==", userId));
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map((doc) => ({
    ...doc.data(),
    id: doc.id,
  })) as StratifyDocument[];
};

/**
 * Fetches a specific page content for a document
 */
export const getDocumentPage = async (
  documentId: string,
  pageNumber: number
): Promise<PageContent> => {
  const pageRef = doc(
    db,
    "documents",
    documentId,
    "pages",
    pageNumber.toString()
  );
  const pageSnap = await getDoc(pageRef);
  if (!pageSnap.exists()) {
    throw new Error("Page not found");
  }
  return pageSnap.data() as PageContent;
};

/**
 * Deletes a document and its associated data (file, insights, pages, logs)
 */
export const deleteDocument = async (
  _userId: string,
  documentId: string,
  _filename: string
): Promise<void> => {
  await apiClient.delete(`/documents/${documentId}`);
};

export const updateDocumentCategory = async (
  documentId: string,
  categoryId: string,
  rerunInsights?: boolean
): Promise<StratifyDocument> => {
  const { data } = await apiClient.put<StratifyDocument>(
    `/documents/${documentId}/category`,
    { categoryId, rerunInsights }
  );
  return data;
};

export const updateDocumentAddress = async (
  documentId: string,
  address: string
): Promise<StratifyDocument> => {
  const { data } = await apiClient.put<StratifyDocument>(
    `/documents/${documentId}/address`,
    { address }
  );
  return data;
};

export const updateDocumentStrataPlan = async (
  documentId: string,
  strataPlanNumber: string
): Promise<StratifyDocument> => {
  const { data } = await apiClient.put<StratifyDocument>(
    `/documents/${documentId}/strata-plan`,
    { strataPlanNumber }
  );
  return data;
};

/**
 * Subscribe to document changes for a user
 */
export const subscribeToDocuments = (
  userId: string,
  onNext: (documents: StratifyDocument[]) => void,
  onError?: (error: Error) => void
): (() => void) => {
  const documentsRef = collection(db, "documents");
  const q = query(documentsRef, where("uploadedBy", "==", userId));

  return onSnapshot(
    q,
    (snapshot: QuerySnapshot) => {
      const documents = snapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      })) as StratifyDocument[];
      onNext(documents);
    },
    onError
  );
};

/**
 * Fetches splitting suggestions for a specific document.
 */
export const getSplittingSuggestions = async (
  documentId: string
): Promise<SplittingSuggestion[]> => {
  const suggestionsRef = collection(
    db,
    "documents",
    documentId,
    "splittingSuggestions"
  );
  const querySnapshot = await getDocs(suggestionsRef);

  return querySnapshot.docs.map((doc) => {
    const data = doc.data();
    // Spread data first to get all fields, then add the id
    return {
      ...data, // Contains documentName, startPage, endPage, savedAt
      id: doc.id,
    } as SplittingSuggestion; // Cast to ensure type correctness
  });
};

/**
 * Initiates the splitting of a document into multiple child documents.
 */
export const splitDocument = async (
  documentId: string,
  splits: SplitDefinition[]
): Promise<{ message: string; parentDocumentId: string }> => {
  // The backend returns 202 Accepted, so we don't expect the full document back
  const { data } = await apiClient.post<{
    message: string;
    parentDocumentId: string;
  }>(`/documents/${documentId}/split`, { splits });
  return data;
};
